import { DeviceIdentificationType, TachographDriverStatus } from '../enums';
import {
    DecodedTrackerPositionMessage,
    IHydratedPosition,
    MobilePositionMessage,
    ITachographData,
    TachographDecodedDataInput,
} from '../interfaces';
import { GPSTrackingDeviceMetadata, MobileAppMetadata } from '../interfaces/device-metadata.interface';
import { PositionDto } from '../types';

export const isMobilePositionMessage = (message: PositionDto | IHydratedPosition): message is MobilePositionMessage => {
    // PositionDto
    if ('phoneNumber' in message) {
        return true;
    }
    // IHydratedPosition
    if ('osVersion' in message || 'deviceModel' in message) {
        return true;
    }
    return false;
};
export const isTrackerPositionMessage = (
    message: PositionDto | IHydratedPosition,
): message is DecodedTrackerPositionMessage => {
    // PositionDto
    if ('licensePlate' in message) {
        return true;
    }
    // IHydratedPosition
    if ((message as IHydratedPosition)?.identifier?.type === DeviceIdentificationType.LICENSE_PLATE) {
        return true;
    }
    return false;
};

export const isPositionMessage = (message: PositionDto): message is PositionDto =>
    'latitude' in message && 'longitude' in message;

export const extractMetadataFromPosition = (
    position: IHydratedPosition,
): Partial<MobileAppMetadata | GPSTrackingDeviceMetadata> => {
    if (isMobilePositionMessage(position)) {
        const metadata: Partial<MobileAppMetadata> = {
            appName: position.appName,
            deviceModel: position.deviceModel,
            locationPermission: position.locationPermission,
            batteryLevel: position.batteryLevel,
            osVersion: position.osVersion,
            appVersion: position.appVersion,
        };
        return metadata;
    } else if (isTrackerPositionMessage(position)) {
        const metadata: Partial<GPSTrackingDeviceMetadata> = {
            trackerDeviceType: position.trackerDeviceType,
            firmwareVersion: position.firmwareVersion,
            signalStrength: position.signalStrength,
            satelliteCount: position.satelliteCount,
            batteryLevel: position.batteryLevel,
            temperature: position.temperature,
        };
        return metadata;
    }
    return {};
};

export const extractPositionMetadata = (
    position: IHydratedPosition,
): Partial<MobileAppMetadata | GPSTrackingDeviceMetadata> | undefined => {
    if (isMobilePositionMessage(position)) {
        const metadata: Partial<MobileAppMetadata> = {
            appName: position.appName,
            deviceModel: position.deviceModel,
            locationPermission: position.locationPermission,
            batteryLevel: position.batteryLevel,
            osVersion: position.osVersion,
            appVersion: position.appVersion,
            accuracy: position.accuracy,
            speedAccuracy: position.speedAccuracy,
        };
        return metadata;
    } else if (isTrackerPositionMessage(position)) {
        const metadata: Partial<GPSTrackingDeviceMetadata> = {
            trackerDeviceType: position.trackerDeviceType,
            firmwareVersion: position.firmwareVersion,
            signalStrength: position.signalStrength,
            satelliteCount: position.satelliteCount,
            batteryLevel: position.batteryLevel,
            temperature: position.temperature,
        };
        return metadata;
    }
    return undefined;
};

export const mapTachographDecodedToHydratedTachographData = (
    decodedData: TachographDecodedDataInput,
): Partial<ITachographData> => {
    const driverStatusMapping: Record<string, TachographDriverStatus> = {
        Driving: TachographDriverStatus.DRIVING,
        Working: TachographDriverStatus.WORKING,
        Available: TachographDriverStatus.AVAILABLE,
        Resting: TachographDriverStatus.RESTING,
        Error: TachographDriverStatus.ERROR,
    };

    const mapDriverStatus = (status?: string): TachographDriverStatus | null => {
        if (!status) return null;
        return driverStatusMapping[status] || null;
    };

    return {
        inMovement: decodedData.inMovement,
        driverStatus: mapDriverStatus(decodedData.driverStatus),
        cardStatus: decodedData.cardStatus,
        dailyDrivingTime: decodedData.dailyDrivingTime,
        weeklyDrivingTime: decodedData.weeklyDrivingTime,
        cumulativeDrivingTime: decodedData.cumulativeWeeklyDrivingTime,
        remainingCurrentDrivingTime: decodedData.remainingDrivingTime,
        remainingShiftDrivingTime: decodedData.remainingShiftDrivingTime,
        remainingWeeklyDrivingTime: decodedData.remainingWeeklyDrivingTime,
        timeLeftUntilDailyRest: decodedData.timeUntilNewDailyRest,
        timeLeftUntilWeeklyRest: decodedData.timeUntilNewWeeklyRestPeriod,
        nextBreakDuration: decodedData.nextBreakDuration,
        timeUntilNextBreak: decodedData.remainingTimeUntilNextBreak,
        driverCardIdLsb: decodedData.driverIdLSB,
        driverCardIdMsb: decodedData.driverIdMSB,
        dailyDrivingTimeExceeds: decodedData.driverDaily9hExceedCount,
        timestamp: new Date().toISOString(),
    };
};

export const getCardIdFromTachographData = (data: Partial<ITachographData>): string | null => {
    const { driverCardIdLsb, driverCardIdMsb } = data;
    if (!driverCardIdLsb || !driverCardIdMsb) return null;
    return `${driverCardIdMsb}${driverCardIdLsb}`.substring(0, 10);
};

export const cardIdToNationalId = (cardId: string): string => {
    return cardId.substring(1);
};
